import { ChevronLeft as ArrowBackIosIcon, ChevronRight as ArrowForwardIosIcon, ShoppingCart } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import FormatPrice from "../../../../components/FormatPrice.jsx";
import { addProductToCart } from "../../../products/ProductToCart.jsx";
import { useDomain } from "../../../../context/DomainContext.jsx";
import { useCart } from "../../../../context/CartContext.jsx";
import { useDispatch } from "react-redux";
import { setSelectedProduct } from "../../../../api/reducers/selectedProductReducer.jsx";
import RetailRichTextDescription from "../../RetailRichTextDescription";

const ScrollableFeaturedProducts = ({ productList, navigate, loggedIn }) => {
  const { addToCart, updateCart } = useCart();
  const domain = useDomain();
  const dispatch = useDispatch();
  const scrollContainerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const viewProductDetail = (product) => {
    if (loggedIn) {
      dispatch(setSelectedProduct(product));
      navigate("/product-info");
    } else {
      navigate("/sign-in");
    }
  };

  const handleProductClick = (product) => {
    if (loggedIn) {
      if (domain === "EDUCATION") {
        viewProductDetail(product);
      } else {
        addProductToCart({
          addToCart,
          navigate,
          productDetails: product,
          quantity: 1,
          updateCart,
        });
      }
    } else {
      navigate("/sign-in");
    }
  };

  const scrollByProductCount = (count) => 250 * count;

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({
      left: -scrollByProductCount(2),
      behavior: "smooth",
    });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({
      left: scrollByProductCount(2),
      behavior: "smooth",
    });
  };

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setAtStart(scrollLeft === 0);
      setAtEnd(scrollLeft + clientWidth >= scrollWidth);
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    scrollContainer?.addEventListener("scroll", checkScrollPosition);
    checkScrollPosition();
    return () => {
      scrollContainer?.removeEventListener("scroll", checkScrollPosition);
    };
  }, []);

  return (
    <div className="relative mt-8 px-6">
      <h2 className="text-4xl font-bold mb-6 text-center text-teal-700">
        Featured Products
      </h2>
      <div className="flex items-center relative">
        {!atStart && (
          <button
            className="absolute left-0 bg-teal-600 text-white p-3 rounded-full shadow-lg z-10 hover:bg-teal-700 transition"
            onClick={scrollLeft}
          >
            <ArrowBackIosIcon fontSize="large" />
          </button>
        )}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto space-x-6 px-6 scrollbar-hide"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {productList?.map((productItem) => (
            <div
              key={productItem?._id}
              className="min-w-[220px] md:min-w-[260px] bg-white shadow-lg rounded-xl overflow-hidden transform hover:scale-105 transition p-5 border border-teal-200"
              style={{ minHeight: "440px", height: "auto", scrollSnapAlign: "start" }}
            >
              <img
                loading="lazy"
                src={productItem?.imageUrlsList?.[0] || "/default-image.jpg"}
                alt={productItem?.name || "Product"}
                className="h-48 w-full object-cover rounded-md mb-3 shadow-md"
              />
              <h3
                className="text-lg font-semibold text-teal-600 cursor-pointer hover:text-teal-700"
                onClick={() => viewProductDetail(productItem)}
              >
                {productItem.name}
              </h3>
              <RetailRichTextDescription content={productItem.description} className="mt-1" />
              <div className="mt-3 text-xl font-medium text-gray-800">
                {productItem.price && (
                  <FormatPrice
                    code={productItem.price.currency || "USD"}
                    price={productItem.price.priceAmt}
                  />
                )}
              </div>
              <button
                onClick={() => handleProductClick(productItem)}
                className="mt-3 bg-teal-600 text-white rounded-full px-4 py-2 w-full flex items-center justify-center hover:bg-teal-700 transition"
              >
                <span>{domain === "EDUCATION" ? "View Schedule" : "Add to Cart"}</span>
                <ShoppingCart className="ml-2" />
              </button>
            </div>
          ))}
        </div>
        {!atEnd && (
          <button
            className="absolute right-0 bg-teal-600 text-white p-3 rounded-full shadow-lg z-10 hover:bg-teal-700 transition"
            onClick={scrollRight}
          >
            <ArrowForwardIosIcon fontSize="large" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ScrollableFeaturedProducts;
